/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Grid,
  IconButton,
  Button,
  Box,
  CircularProgress
} from '@material-ui/core'
import { GridCellEditCommitParams, GridValueGetterParams } from '@material-ui/data-grid'
import { CloudDownload, Edit } from '@material-ui/icons'
import { ServerError } from '../../../schemas/Error'
import { Third } from '../../../schemas/Third'
import ThirdService from '../../../services/Third'
import PageContainer from '../Common/PageContainer'
import ResponsiveList, { ResponsiveColumn } from '../Common/ResponsiveList'
import { urls } from '../../../urls'

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  loadMoreButton: {
    minHeight: 44,
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  loadingContainer: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: '0',
    right: '0',
    background: '#BFBFBFBF',
    opacity: 0.6,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '50vh'
  }
}))

// - in this part all the component texts are housed
const texts = {
  header: {
    description: 'Lista de terceros'
  },
  body: {
    buttonLoadMore: {
      label: 'Cargar más ...'
    }
  }
}

// widthPct: porcentaje del ancho de la tabla (las columnas visibles siempre suman 100%)
const columns: ResponsiveColumn[] = [
  {
    field: 'document',
    headerName: 'Documento',
    editable: false,
    widthPct: 15
  },
  {
    field: 'fullName',
    headerName: 'Nombre',
    editable: false,
    widthPct: 30,
    cardTitle: true,
    valueGetter: (params: GridValueGetterParams) =>
      `${params.getValue(params.id, 'name') || params.getValue(params.id, 'businessName')} ${params.getValue(params.id, 'lastname') || ''
      }`,
  },
  {
    field: 'email',
    headerName: 'Email',
    widthPct: 25,
    editable: true,
  },
  {
    field: 'phone',
    headerName: 'Teléfono',
    widthPct: 15,
    editable: true,
    hideOnTablet: true,
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    widthPct: 15,
    type: 'actions',
    renderCell: (params: any) => (
      <ActionButtons params={params} />
    )
  }
]

// ------------ Init state -----------
interface State {
  loadMoreButtonDisabled: boolean
  lastEvaluatedKey?: string
  thirds: Third[]
  loading: boolean
}

const initState: State = {
  loadMoreButtonDisabled: false,
  thirds: [],
  loading: true
}

function ActionButtons(props: any) {
  const navigate = useNavigate()

  const onEdit = () => {
    navigate(`/${urls.app.index}/${urls.app.main.third.form}?document=${props.params.row.document ?? ''}`)
  }

  return (
    <IconButton
      type="submit"
      onClick={() => onEdit()}
    >
      <Edit fontSize={"small"} color="primary" />
    </IconButton>
  )
}

export default function ThirdList() {

  const classes = useStyles()

  const [state, setState] = useState<State>(initState)

  React.useEffect(() => {
    async function loadData() {
      const thirdService = new ThirdService()
      const response = await thirdService.getThirds({ limit: 10 })
      setState({
        ...state,
        thirds: response.thirds,
        lastEvaluatedKey: response.lastEvaluatedKey,
        loadMoreButtonDisabled: response.lastEvaluatedKey !== undefined ? false : true,
        loading: false
      })
    }
    loadData()
  }, [])

  const onLoadMore = async () => {
    try {
      const thirdService = new ThirdService()
      const response = await thirdService.getThirds({ limit: 10, lastEvaluatedKey: state.lastEvaluatedKey })
      setState({
        ...state,
        thirds: state.thirds.concat(response.thirds),
        lastEvaluatedKey: response.lastEvaluatedKey,
        loadMoreButtonDisabled: response.lastEvaluatedKey !== undefined ? false : true
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError && serverError.response) {
          return toast.error(serverError.response.data.message)
        }
      }
    }
  }

  const gridEdit = async (params: GridCellEditCommitParams) => {

    let thirdToEdit: any = {}

    const selectedThird = state.thirds.find(third => third.document === params.id)
    if (selectedThird !== undefined) {
      thirdToEdit = {
        ...selectedThird,
        [params.field]: params.value
      }
    } else {
      return
    }

    try {
      const thirdService = new ThirdService()
      const thirdUpdated = await thirdService.updateThird(thirdToEdit)

      return toast.success(`El tercero ${thirdUpdated.document} fue editado con éxito`)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError && serverError.response) {
          return toast.error(serverError.response.data.message)
        }
      }
    }
  }

  return (
    <PageContainer title={texts.header.description}>
      <Grid container spacing={3}>

        {/* ----- Listado: tabla (>= 600px) o cards (mobile) ------*/}
        <Grid item xs={12}>
          <ResponsiveList
            rows={state.thirds}
            columns={columns}
            keyId='document'
            gridEdit={gridEdit}
            cardActions={(row) => <ActionButtons params={{ row }} />}
          />
        </Grid>

        <Grid item xs={12} className={classes.alignRight}>
          <Button
            type="button"
            color="primary"
            className={classes.loadMoreButton}
            startIcon={<CloudDownload />}
            disabled={state.loadMoreButtonDisabled}
            onClick={() => onLoadMore()}
          >
            {texts.body.buttonLoadMore.label}
          </Button>
        </Grid>
      </Grid>
      {
        state.loading ?
        (
          <div className={classes.loadingContainer}>
            <Box>
              <CircularProgress/>
            </Box>
          </div>
        ) : ''
      }
    </PageContainer>
  )
}
